/*
 * File: Support.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on June 13, 2024 at 9:21 AM
 * Copyright © 2024 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 11, 2025 at 2:29 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Masonry from 'react-responsive-masonry';

// Utilities
import { buildDynamicMeta } from '../../../components/Meta';

// Services
import { fetchResources } from '../../../services/resource';
import { recordView } from '../../../services/view';

// Store
import { fetchPrompts } from '../../../store/slices/resourcePrompt/resourcePrompt.slice';
import { updateResources, updateFeaturedResources, updateActionSortBy } from '../../../store/slices/resource/resource.slice';

// Components
import {
	Meta,
	AppNavigation,
	Typography,
	Emoji,
	LocalitySelector,
	SchemaScript,
	ResourceCard,
	Tag,
	LocaleLink,
	FeedActionDropdown,
	InfiniteScroll,
	ResponsiveMasonry
} from '../../../components';

// Constants
import { AVAILABLE_I18N_NAMESPACES, SOURCES } from '../../../../constants/base.constants';
import { RESOURCES_FEED_SORT_BY } from '../../../../constants/feed.constants';
import { masonryFourBreakpoints } from '../../../styles/constants';

// Styles
import * as S from './Support.styles';


/**
 * Constants
 */

const PLACEHOLDER_PROMPTS = [
	{ label: 'I feel lost' },
	{ label: 'I need answers' },
	{ label: 'I don\'t know what happened to me' },
	{ label: 'I want to try in-person support' },
	{ label: 'I need a place to go' },
	{ label: 'I want to talk to someone' }
];

const SHOW_PROMPTS = false;


/**
 * Component
 */

const Support = ({ meta: m }) => {

	// Get resources from hook
	const {
		feedStatus,
		featuredStatus,
		items: resources,
		featuredItems: featuredResources,
		actionSortBy
	} = useSelector((state) => state.resource);

	// Create state handlers
	const [currentPage, setCurrentPage] = useState(0);
	const [hasMoreToLoad, setMoreToLoad] = useState(resources.length === 0 || resources.length > 10);
	const [isFeedLoading, setIsFeedLoading] = useState(false);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current resource prompts from hook
	const { status: promptStatus, value: resourcePrompts } = useSelector((state) => state.resourcePrompt);

	// Create references
	const firstUpdateFilter = useRef(true);

	// Use hooks
	const dispatch = useDispatch();

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Define meta content
	const meta = useMemo(() => ({
		title: t('pageSupport.metaTitle', { partnerName: partner.name }),
		description: t('pageSupport.metaDescription', { partnerName: partner.name }),
		bodyClasses: 'hide-captcha',
		...m || {}
	}), [m, partner, t]);

	// Create partner domain
	const partnerDomain = useMemo(() => {
		let domain = `https://${partner.domain}`;
		if (partner.redirect_to_subdomain) {
			domain = `https://${partner.redirect_to_subdomain}.${partner.domain}`;
		}
		return domain;
	}, [partner]);

	// Create feed action options
	const feedSortByOptions = useMemo(() => RESOURCES_FEED_SORT_BY.map((option) => ({
		label: t(`feedActions.${option}`),
		value: option
	})), [t]);

	// Handle fetch resources
	const fetchSupportResources = async ({ page }) => {

		// Update state
		setIsFeedLoading(true);

		// Fetch resources
		const { data: { result: fetchedResources } } = await fetchResources({
			page,
			sortBy: actionSortBy,
			isFeatured: false
		});

		// Update resources
		dispatch(updateResources(page === 0 ? fetchedResources : [...resources, ...fetchedResources]));

		// Update page state
		setCurrentPage(page);

		// Update more to load state
		setMoreToLoad(fetchedResources.length > 10);

		// Update state
		setIsFeedLoading(false);
	};

	// Handle fetch featured resources
	const fetchFeaturedResources = async () => {

		// Fetch resources
		const { data: { result: fetchedResources } } = await fetchResources({
			page: 0,
			isFeatured: true
		});

		// Update resources
		dispatch(updateFeaturedResources(fetchedResources));
	};

	// Handle actions on component load
	useEffect(() => {
		if (!firstUpdateFilter.current) {

			// Fetch resources if necessary
			if (feedStatus === 'final') {

				// Fetch resources
				fetchSupportResources({ page: 0 });
			}
		}

		// Update first update
		firstUpdateFilter.current = false;

	}, [actionSortBy]); // eslint-disable-line react-hooks/exhaustive-deps

	// Handle actions on feed status change
	useEffect(() => {

		// Fetch resources if necessary
		if (feedStatus !== 'final') {

			// Fetch resources
			fetchSupportResources({ page: currentPage });
		}

	}, [feedStatus]);

	// Handle actions on featured feed status change
	useEffect(() => {

		// Fetch featured resources if necessary
		if (featuredStatus !== 'final') {

			// Fetch featured resources
			fetchFeaturedResources();
		}

	}, [featuredStatus]);

	// Handle actions on component load
	useEffect(() => {

		// Fetch resource prompts if necessary
		if (promptStatus !== 'final') {

			// Fetch resource prompts
			dispatch(fetchPrompts());
		}

		// Record view
		recordView({
			source: SOURCES.RESOURCES,
			urlPath: window.location.pathname
		});

	}, []);

	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} data={{}} />

			{/* Schema.org */}
			<SchemaScript schema={{
				'@context': 'http://schema.org',
				'@type': 'BreadcrumbList',
				'@id': `${partnerDomain}/resources/#BreadcrumbList`,
				itemListElement: [
					{
						'@type': 'ListItem',
						position: 1,
						item: {
							'@id': `${partnerDomain}/resources/#ListItem`,
							name: buildDynamicMeta(meta.title, { partner }),
							url: `${partnerDomain}${locale.localePath}/resources`
						}
					}
				],
			}}
			/>

			{/* Component Content */}
			<AppNavigation showFooter>
				<S.Wrapper>

					{/* Header */}
					<S.Header>

						{/* Title */}
						<Typography tag="h1" weight="black">
							<Trans t={t} i18nKey="pageSupport.title">
								Find Support
							</Trans>
						</Typography>

						{/* Locality Selector */}
						<LocalitySelector />

					</S.Header>

					{/* Section - Featured Resources */}
					{!(featuredStatus === 'final' && featuredResources.length === 0) && (
						<S.FeatureSection>

							{/* Title */}
							<Typography tag="h3" weight="black">
								<Trans t={t} i18nKey="pageSupport.featuredTitle">
									Featured Resources
								</Trans>
							</Typography>

							{/* Content Masonry */}
							{featuredStatus === 'final' ? (
								<ResponsiveMasonry
									defaultBreakpoint={4}
									breakpoints={masonryFourBreakpoints}
								>
									<Masonry gutter="22px">
										{featuredResources.map((resource, index) => (
											<ResourceCard
												key={resource.resource_id}
												resource={resource}
												source={SOURCES.RESOURCES}
												index={index}
											/>
										))}
									</Masonry>
								</ResponsiveMasonry>
							) : (
								<ResponsiveMasonry
									style={{ marginTop: resources.length > 0 ? '22px' : '0px' }}
									defaultBreakpoint={4}
									breakpoints={masonryFourBreakpoints}
								>
									<Masonry gutter="22px">
										<ResourceCard isPlaceholder index={0} />
										<ResourceCard isPlaceholder index={1} />
										<ResourceCard isPlaceholder index={2} />
										<ResourceCard isPlaceholder index={3} />
									</Masonry>
								</ResponsiveMasonry>
							)}

						</S.FeatureSection>
					)}

					{/* Section - Resource Prompts */}
					{SHOW_PROMPTS && (
						<S.FeatureSection>

							{/* Title */}
							<Typography tag="h3" weight="black">
								<Trans t={t} i18nKey="pageSupport.promptsTitle">
									How are you feeling?
								</Trans>
							</Typography>

							{/* Resource Prompts */}
							{promptStatus === 'final' ? (
								<S.PromptContainer>
									{resourcePrompts.map((prompt) => (
										<LocaleLink to={`/resources/p/${prompt.slug}`} key={prompt.prompt_id}>
											<Tag>
												<Emoji symbol={prompt.decoration_locales[locale.localeBaseId]} label={prompt.prompt_locales[locale.localeBaseId]} size={1.2} />
												<Typography>{prompt.prompt_locales[locale.localeBaseId]}</Typography>
											</Tag>
										</LocaleLink>
									))}
								</S.PromptContainer>
							) : (
								<S.PromptContainer>
									{PLACEHOLDER_PROMPTS.map((placeholder) => (
										<S.PromptPlaceholder className="shimmer shimmer-element" key={placeholder.label}>
											<Typography>{placeholder.label}</Typography>
										</S.PromptPlaceholder>
									))}
								</S.PromptContainer>
							)}
						</S.FeatureSection>
					)}

					{/* Section - Resources */}
					<S.FeatureSection>

						{/* Title */}
						<Typography tag="h3" weight="black">
							<Trans t={t} i18nKey="pageSupport.resourcesTitle">
								Resources
							</Trans>
						</Typography>

						{/* Feed Action Bar */}
						<S.FeedActionBar>

							{/* Feed Sort Action */}
							<FeedActionDropdown
								title={t('feedActions.sortByTitle')}
								selected={actionSortBy}
								selectedTitle={t(`feedActions.${actionSortBy}`)}
								options={feedSortByOptions}
								multipleSelection={false}
								widgetWidth={170}
								onSelect={({ value }) => { dispatch(updateActionSortBy(value)); }}
							/>

						</S.FeedActionBar>

						{/* Infinite Scroll */}
						<InfiniteScroll
							dataLength={resources.length}
							next={() => { if (!isFeedLoading) fetchSupportResources({ page: currentPage + 1 }); }}
							refresh={async () => { await fetchSupportResources({ page: 0 }); return true; }}
							loader={(
								<ResponsiveMasonry
									style={{ marginTop: resources.length > 0 ? '22px' : '0px' }}
									defaultBreakpoint={4}
									breakpoints={masonryFourBreakpoints}
								>
									<Masonry gutter="22px">
										<ResourceCard isPlaceholder index={0} />
										<ResourceCard isPlaceholder index={1} />
										<ResourceCard isPlaceholder index={2} />
										<ResourceCard isPlaceholder index={3} />
										<ResourceCard isPlaceholder index={4} />
										<ResourceCard isPlaceholder index={5} />
										<ResourceCard isPlaceholder index={6} />
										<ResourceCard isPlaceholder index={7} />
									</Masonry>
								</ResponsiveMasonry>
							)}
							hasMore={hasMoreToLoad}
						>

							{/* Content Masonry */}
							<ResponsiveMasonry
								defaultBreakpoint={4}
								breakpoints={masonryFourBreakpoints}
							>
								<Masonry gutter="22px">
									{resources.map((resource, index) => (
										<ResourceCard
											key={resource.resource_id}
											resource={resource}
											source={SOURCES.RESOURCES}
											index={index}
										/>
									))}
								</Masonry>
							</ResponsiveMasonry>

						</InfiniteScroll>

					</S.FeatureSection>
				</S.Wrapper>
			</AppNavigation>
		</>
	);
};


/**
 * Configuration
 */

Support.propTypes = {
	meta: PropTypes.shape()
};
Support.defaultProps = {
	meta: {}
};


/**
 * Exports
 */

export default Support;
